import { Volume2, Square } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTts } from "../hooks/useTts";
import { useVoiceEnabled } from "../hooks/useVoiceEnabled";

interface SpeakButtonProps {
  messageId: string;
  text: string;
  /** Override the default button styling (the Hub uses its own action-button class). */
  className?: string;
}

// `useTts()` internally calls `useAssistantProfile()` (react-query), which
// requires a QueryClientProvider ancestor. Gating on `useVoiceEnabled` (a
// plain fetch/useState hook, no react-query) FIRST means useTts never runs
// at all when TTS is disabled — so a host that hasn't enabled TTS (or a test
// that renders MessageBubble without a QueryClientProvider) never needs one.
export function SpeakButton({ messageId, text, className }: SpeakButtonProps) {
  const { ttsEnabled } = useVoiceEnabled();
  if (!ttsEnabled || !text.trim()) return null;
  return <SpeakButtonControl messageId={messageId} text={text} className={className} />;
}

function SpeakButtonControl({ messageId, text, className }: SpeakButtonProps) {
  const { t } = useTranslation();
  const { speak, stop, isSpeaking } = useTts();
  const speaking = isSpeaking(messageId);
  const label = speaking ? t("assistant.tts.stop") : t("assistant.tts.speak");

  return (
    <button
      type="button"
      onClick={() => (speaking ? stop() : speak(messageId, text))}
      title={label}
      aria-label={label}
      className={
        className ??
        "inline-flex items-center justify-center size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
      }
    >
      {speaking ? <Square className="size-4" /> : <Volume2 className="size-4" />}
    </button>
  );
}
